import React from "react";
import { Modal, ModalHeader, ModalBody, Spinner, Badge } from "reactstrap";
import { useFundamentalsDetail, formatMarketCap } from "../hooks/useFundamentals";

interface Props {
  ticker: string;
  isOpen: boolean;
  toggle: () => void;
}

function formatFloatShares(shares: number | null | undefined): string {
  if (!shares) return "—";
  if (shares >= 1e9) return `${(shares / 1e9).toFixed(2)}B`;
  if (shares >= 1e6) return `${(shares / 1e6).toFixed(1)}M`;
  return shares.toLocaleString();
}

/**
 * Modal showing the full company profile for a ticker — sector, industry,
 * market cap, float shares, and business description — fetched on demand
 * from /api/fundamentals/<ticker> (only the lightweight summary is preloaded
 * elsewhere via useFundamentals).
 */
export default function CompanyProfileModal({ ticker, isOpen, toggle }: Props) {
  const { detail, loading, error } = useFundamentalsDetail(isOpen ? ticker : null);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>
        {ticker} — Company Profile
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="text-center py-4">
            <Spinner size="sm" color="secondary" />
          </div>
        )}

        {!loading && error && (
          <p className="text-muted mb-0">Company profile is not available for {ticker}.</p>
        )}

        {!loading && !error && detail && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {detail.sector && <Badge color="light" className="text-muted fw-normal">{detail.sector}</Badge>}
              {detail.industry && <Badge color="light" className="text-muted fw-normal">{detail.industry}</Badge>}
            </div>

            <Row label="Market Cap" value={formatMarketCap(detail.market_cap) || "—"} />
            <Row label="Float Shares" value={formatFloatShares(detail.float_shares)} />

            {detail.description && (
              <div className="mt-3">
                <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  About
                </div>
                <p className="mb-0" style={{ fontSize: 14, lineHeight: 1.5 }}>
                  {detail.description}
                </p>
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="d-flex justify-content-between border-bottom py-1">
      <span className="text-muted" style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
}
